<script setup lang="ts">
import { router, usePage } from "@inertiajs/vue3"
import { computed, ref } from "vue"
import route from "ziggy-js"
import { Pencil, Plus, Printer, Trash2 } from "lucide-vue-next"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/components/ui/toast/use-toast"
import { toHuman } from "@/helpers/formatters/currency"
import AppLayout from "@/layouts/AppLayout.vue"
import CategoryManagerDialog from "./CategoryManagerDialog.vue"
import RecipeCard from "./RecipeCard.vue"
import RecipeFormDialog from "./RecipeFormDialog.vue"
import type { DrinkLayer } from "./DrinkDiagram.vue"

defineOptions({
    layout: [AppLayout],
})

type Ingredient = {
    id: number
    name: string
    unit: string
    quantity: number
    note: string | null
    unit_cost_in_cents: number
    line_cost_in_cents: number
    quantity_on_hand: number
    reorder_level: number
    in_stock: boolean
    status: "in_stock" | "low_stock" | "out_of_stock"
}

type Recipe = {
    id: number
    name: string
    code: string
    category: string
    description: string
    size: string
    is_iced: boolean
    is_food: boolean
    image_url: string | null
    preparation: string[]
    options: string[]
    layers: DrinkLayer[]
    selling_price_in_cents: number
    cost_in_cents: number
    margin_in_cents: number
    margin_percent: number
    can_make: number
    stock_status: "in_stock" | "low_stock" | "out_of_stock"
    ingredients: Ingredient[]
    api: {
        update_path: string
        delete_path: string
    }
}

type IngredientOption = {
    id: number
    name: string
    unit: string
    unit_cost_in_cents: number
}

type Props = {
    recipes: { data: Recipe[] }
    categories: string[]
    recipeCategories: { id: number; name: string; is_food: boolean }[]
    branchId: number
    ingredients: IngredientOption[]
}

const props = defineProps<Props>()
const page = usePage()
const { toast } = useToast()
const isAdmin = computed(() => Boolean((page.props.user as { is_admin?: boolean } | null)?.is_admin))

const branches = computed(() => page.props.branches as { id: number; name: string }[])
const hasMultipleBranches = computed(() => branches.value.length > 1)
const selectedCategory = ref<string>("All")
const formOpen = ref(false)
const categoriesOpen = ref(false)
const editingRecipe = ref<Recipe | null>(null)

const filteredRecipes = computed(() => {
    if (selectedCategory.value === "All") {
        return props.recipes.data
    }

    return props.recipes.data.filter((recipe) => recipe.category === selectedCategory.value)
})

const categoryTabs = computed(() => ["All", ...props.categories])

function onBranchChange(branchId: string) {
    router.get(
        route("recipes.index"),
        { branch_id: branchId },
        { preserveState: true, preserveScroll: true }
    )
}

function openCreate() {
    editingRecipe.value = null
    formOpen.value = true
}

function openEdit(recipe: Recipe) {
    editingRecipe.value = recipe
    formOpen.value = true
}

function printRecipe(recipe: Recipe) {
    const root = document.documentElement
    const previousTitle = document.title
    const sections = document.querySelectorAll("[data-recipe-print]")
    let cleaned = false

    root.classList.add("printing-recipe")
    document.title = recipe.is_food || !recipe.code
        ? `${recipe.name} Recipe Card`
        : `${recipe.code} · ${recipe.name} Recipe Card`
    sections.forEach((section) => {
        section.classList.toggle("is-print-target", section.getAttribute("data-recipe-print") === String(recipe.id))
    })

    const cleanup = () => {
        if (cleaned) {
            return
        }

        cleaned = true
        root.classList.remove("printing-recipe")
        document.title = previousTitle
        sections.forEach((section) => section.classList.remove("is-print-target"))
        window.removeEventListener("afterprint", cleanup)
    }

    window.addEventListener("afterprint", cleanup)
    window.setTimeout(() => window.print(), 50)
}

function destroyRecipe(recipe: Recipe) {
    if (!confirm(`Delete "${recipe.name}"? This cannot be undone.`)) {
        return
    }

    router.delete(recipe.api.delete_path, {
        preserveScroll: true,
        onSuccess() {
            toast({
                description: "Recipe deleted.",
            })
        },
    })
}

function stockLabel(status: string) {
    return {
        in_stock: "In Stock",
        low_stock: "Low Stock",
        out_of_stock: "Out of Stock",
    }[status] ?? status
}

function stockBadgeClass(status: string) {
    return {
        in_stock: "bg-emerald-100 text-emerald-800 border-emerald-200",
        low_stock: "bg-amber-100 text-amber-800 border-amber-200",
        out_of_stock: "bg-red-100 text-red-800 border-red-200",
    }[status]
}
</script>

<template>
    <div class="min-h-screen bg-[#f3f1ea] -mx-4 sm:-mx-6 lg:-mx-8 print:mx-0 print:min-h-0 print:bg-white">
        <div class="print-hide bg-[rgb(181_115_5)] text-white">
            <div class="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
                <div class="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                    <div>
                        <h1 class="font-recipe text-4xl font-bold tracking-tight">Recipe Cards</h1>
                        <p class="mt-2 max-w-xl text-white/80">
                            Build standards for every size, with live costing{{ hasMultipleBranches ? " and stock by branch" : "" }}.
                        </p>
                    </div>

                    <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div v-if="hasMultipleBranches" class="flex items-center gap-3">
                            <span class="text-sm text-white/70">Branch</span>
                            <Select :model-value="String(branchId)" @update:model-value="onBranchChange">
                                <SelectTrigger class="w-[220px] border-white/20 bg-white/10 text-white">
                                    <SelectValue placeholder="Select branch" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem v-for="branch in branches" :key="branch.id" :value="String(branch.id)">
                                        {{ branch.name }}
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <Button v-if="isAdmin" variant="outline" class="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" @click="categoriesOpen = true">
                            Manage Categories
                        </Button>
                        <Button v-if="isAdmin" class="bg-white text-[rgb(181_115_5)] hover:bg-white/90" @click="openCreate">
                            <Plus class="mr-1 h-4 w-4" />
                            Add Recipe
                        </Button>
                    </div>
                </div>
            </div>
        </div>

        <div class="print-hide sticky top-0 z-10 border-b bg-[#f3f1ea]/95 backdrop-blur">
            <div class="mx-auto max-w-6xl px-4 sm:px-6">
                <div class="flex gap-1 overflow-x-auto py-3">
                    <button
                        v-for="category in categoryTabs"
                        :key="category"
                        class="whitespace-nowrap rounded-full px-5 py-2 text-sm font-semibold transition-colors"
                        :class="
                            selectedCategory === category
                                ? 'bg-[rgb(181_115_5)] text-white'
                                : 'text-gray-600 hover:bg-white'
                        "
                        @click="selectedCategory = category"
                    >
                        {{ category }}
                    </button>
                </div>
            </div>
        </div>

        <div class="mx-auto max-w-6xl space-y-8 px-4 py-8 sm:px-6 print:max-w-none print:space-y-0 print:p-0">
            <section
                v-for="recipe in filteredRecipes"
                :key="recipe.id"
                class="recipe-print-root space-y-3"
                :data-recipe-print="recipe.id"
            >
                <div class="print-hide flex flex-wrap items-center justify-between gap-3 px-1">
                    <div class="flex flex-wrap items-center gap-2 text-xs text-[#1e3932]">
                        <span
                            v-if="recipe.code && !recipe.is_food"
                            class="inline-flex items-center rounded-sm border border-[rgb(181_115_5)] px-2 py-0.5 font-recipe font-bold tracking-[0.16em] text-[rgb(181_115_5)]"
                        >
                            {{ recipe.code }}
                        </span>
                        <span
                            v-if="isAdmin"
                            class="inline-flex items-center rounded-full border px-2.5 py-0.5 font-semibold"
                            :class="stockBadgeClass(recipe.stock_status)"
                        >
                            {{ stockLabel(recipe.stock_status) }}
                        </span>
                        <span v-if="isAdmin" class="text-muted-foreground">
                            {{ toHuman(recipe.selling_price_in_cents / 100) }}
                            · cost {{ toHuman(recipe.cost_in_cents / 100) }}
                            · {{ recipe.margin_percent }}% margin
                            · {{ recipe.can_make }} servings
                        </span>
                    </div>
                    <div class="flex gap-2">
                        <Button variant="outline" size="sm" @click="printRecipe(recipe)">
                            <Printer class="mr-1 h-3.5 w-3.5" />
                            Print
                        </Button>
                        <template v-if="isAdmin">
                            <Button variant="outline" size="sm" @click="openEdit(recipe)">
                                <Pencil class="mr-1 h-3.5 w-3.5" />
                                Edit
                            </Button>
                            <Button variant="outline" size="sm" class="text-destructive" @click="destroyRecipe(recipe)">
                                <Trash2 class="mr-1 h-3.5 w-3.5" />
                                Delete
                            </Button>
                        </template>
                    </div>
                </div>

                <RecipeCard
                    :recipe-id="recipe.id"
                    :name="recipe.name"
                    :code="recipe.code"
                    :category="recipe.category"
                    :size="recipe.size"
                    :is-iced="recipe.is_iced"
                    :is-food="recipe.is_food"
                    :image-url="recipe.image_url"
                    :preparation="recipe.preparation"
                    :options="recipe.options"
                    :layers="recipe.layers"
                    :ingredients="recipe.ingredients"
                />
            </section>

            <p v-if="filteredRecipes.length === 0" class="print-hide py-20 text-center text-muted-foreground">
                No recipes in this category.
                <button v-if="isAdmin" class="ml-1 font-semibold text-[rgb(181_115_5)] hover:underline" @click="openCreate">
                    Add one
                </button>
            </p>
        </div>

        <div v-if="isAdmin" class="print-hide">
            <CategoryManagerDialog
                v-model:open="categoriesOpen"
                :categories="recipeCategories"
                @renamed="(previous, current) => { if (selectedCategory === previous) selectedCategory = current }"
            />
            <RecipeFormDialog
                v-model:open="formOpen"
                :recipe="editingRecipe"
                :ingredients="ingredients"
                :categories="recipeCategories"
            />
        </div>
    </div>
</template>
